import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { useLazyQuery } from "@apollo/client/react";
import { captureException } from "@sentry/react";
import {
  MCLOUD_COMMANDS,
  SCRIBENOTE_FLASK as BASE_URI,
} from "@src/constants";
import { isMCloudEmbedded, setMCloudEmbedded } from "@src/utils";
import axios from "axios";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";

import { GET_NOTE } from "../components/notes/EditNote";
import { useAuth } from "../hooks";

const WidgetContext = createContext({
  isAppHeaderVisible: !isMCloudEmbedded(),
});

export function WidgetProvider({ children }) {
  const auth = useAuth();
  const isEmbedded = isMCloudEmbedded();

  const [widgetLayoutState, setWidgetLayoutState] = useState({
    isAppHeaderVisible: !isEmbedded,
  });
  const lastPostMessageEventRef = useRef(null);

  const [getNote] = useLazyQuery(GET_NOTE, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!isEmbedded) return;

    const fetchNoteAndProcess = async (noteId) => {
      try {
        const { data } = await getNote({
          variables: { uuid: noteId },
        });
        const res = await axios.post(
          `${BASE_URI}/sso/mvp/gpt`,
          { objective: data.note.objective },
          { withCredentials: true },
        );
        const mCloudNote = mapMCloudNote(data);
        mCloudNote.objective_json = JSON.parse(res.data);
        window.parent.postMessage(
          messageFactory(MCLOUD_COMMANDS.CATCH_NOTE_DATA, {
            actionResult: mCloudNote,
            sourceAction: lastPostMessageEventRef?.current?.data,
          }),
          lastPostMessageEventRef?.current?.data?.domain,
        );
      } catch (err) {
        if (!CombinedGraphQLErrors.is(err)) {
          captureException(err);
        }

        window.parent.postMessage(
          messageFactory(
            MCLOUD_COMMANDS.CATCH_NOTE_DATA,
            {
              actionResult: CombinedGraphQLErrors.is(err)
                ? null
                : {
                    error: {
                      message: "Error parsing source objective data",
                    },
                  },
              sourceAction: lastPostMessageEventRef?.current?.data,
            },
            err,
          ),
          lastPostMessageEventRef?.current?.data?.domain,
        );
      }
    };

    const messageHandler = async (event) => {
      if (!event?.data?.action?.includes("com.")) return;

      console.log("Scribenote in mCloud received data: ", event);
      const sourceDomain = event.data.domain; // replace with "*" for local testing
      if (!isMCloudEmbedded()) return; // comment out for local testing

      if (event.data.action === MCLOUD_COMMANDS.FETCH_NOTE_DATA) {
        lastPostMessageEventRef.current = event;
        const noteId = window.location.href.split("/").pop();
        fetchNoteAndProcess(noteId);
      } else if (
        event.data.action === MCLOUD_COMMANDS.CATCH_AUTH0_TOKEN
      ) {
        window.parent.postMessage(
          messageFactory(event.data.action, event.data.payload),
          sourceDomain,
        );

        localStorage.clear();
        setMCloudEmbedded();
        const redirect_url = `${BASE_URI}/sso/mvp/token?token=${event.data.payload["token"]}&signature=${event.data.payload["signature"]}&origin=${window.location.origin}`;
        window.location.href = redirect_url;
      } else if (
        event.data.action === MCLOUD_COMMANDS.CATCH_AUTH_SIGNOUT
      ) {
        console.log(event.data);
        auth.logout();
        localStorage.clear();
        setMCloudEmbedded();
        window.location.href = `${BASE_URI}/sso/mvp/logout&origin=${window.location.origin}`;
      } else if (
        event.data.action === MCLOUD_COMMANDS.SHOW_USER_MENU
      ) {
        setWidgetLayoutState((prev) => ({
          ...prev,
          isAppHeaderVisible: true,
        }));
      } else if (
        event.data.action === MCLOUD_COMMANDS.HIDE_USER_MENU
      ) {
        setWidgetLayoutState((prev) => ({
          ...prev,
          isAppHeaderVisible: false,
        }));
      } else {
        window.parent.postMessage(
          messageFactory(
            event.data.action,
            event.data.payload,
            "Scribenote received unrecognised action request",
          ),
          sourceDomain,
        );
      }
    };

    window.addEventListener("message", messageHandler);
    return () => {
      window.removeEventListener("message", messageHandler);
    };
  }, [isEmbedded, auth, getNote]);

  return (
    <WidgetContext.Provider value={widgetLayoutState}>
      {children}
    </WidgetContext.Provider>
  );
}

const messageFactory = (action, payload, err) => {
  return {
    action: action,
    payload: payload,
    error: err,
  };
};

const mapMCloudNote = (noteResp) => {
  const noteData = noteResp.note;
  return {
    note: {
      id: noteData.uuid,
      createdAt: noteData.createdAt,
      subjective: noteData.subjective,
      objective: noteData.objective,
      assessment: noteData.assessment,
      plan: noteData.plan,
      isApproved: noteData.isApproved,
    },
  };
};

export function useWidgetLayout() {
  return useContext(WidgetContext);
}
